/**
 * useCompose Hook
 *
 * Manages compose state: recipients, subject, body, attachments, draft ID,
 * mode (new/reply/replyAll/forward), and scheduling.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { api, EmailAddress, EmailMessage, EmailMessageDetail } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { traceStart, traceEnd, traceError } from '@/lib/telemetry';
import { useUploadVaultDocument } from '@/arche/arches/vault-documents/hooks/useVaultDocuments';

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

interface AttachmentFile {
  id: string;
  /** Vault document id once the upload completes; null while uploading. */
  documentId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploading: boolean;
}

interface ComposeState {
  to: EmailAddress[];
  cc: EmailAddress[];
  bcc: EmailAddress[];
  subject: string;
  bodyHtml: string;
  attachments: AttachmentFile[];
  draftId: string | null;
  mode: ComposeMode;
  replyToMessageId: string | null;
  signatureHtml: string;
  quotedHtml: string;
}

const INITIAL_STATE: ComposeState = {
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  bodyHtml: '',
  attachments: [],
  draftId: null,
  mode: 'new',
  replyToMessageId: null,
  signatureHtml: '',
  quotedHtml: '',
};

const DRAFT_SAVE_INTERVAL = 30000; // 30 seconds

export function useCompose(accountId: string | undefined, personalHolderId: string | null) {
  const [state, setState] = useState<ComposeState>(INITIAL_STATE);
  const [sending, setSending] = useState(false);
  const dirtyRef = useRef(false);
  const draftTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Single-flight guard. If a prior auto-save is still in flight when the
  // 30-second tick fires, we skip the new save rather than racing two
  // concurrent writes against the same draft row. Without this, a user
  // typing rapidly + switching tabs can produce duplicate draft inserts.
  const draftSaveInFlightRef = useRef(false);
  const { toast } = useToast();
  const uploadVaultDocument = useUploadVaultDocument();

  // Mark dirty on any state change
  const updateState = useCallback((patch: Partial<ComposeState>) => {
    dirtyRef.current = true;
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Auto-save drafts
  useEffect(() => {
    if (!accountId) return;
    draftTimerRef.current = setInterval(() => {
      if (dirtyRef.current && state.subject.trim() + state.bodyHtml.trim()) {
        saveDraftInternal();
      }
    }, DRAFT_SAVE_INTERVAL);
    return () => {
      if (draftTimerRef.current) clearInterval(draftTimerRef.current);
    };
  }, [accountId, state.subject, state.bodyHtml]); // intentionally excludes saveDraftInternal to avoid recreating timer

  const saveDraftInternal = useCallback(async () => {
    if (!accountId) return;
    // De-dupe: a previous save is still running. Drop this tick — the
    // current save is at most 30 s old (one DRAFT_SAVE_INTERVAL), so the
    // next tick will pick up any subsequent edits.
    if (draftSaveInFlightRef.current) return;
    draftSaveInFlightRef.current = true;
    const traceId = traceStart('email.draft.save', { accountId });
    try {
      const fullBody =
        state.bodyHtml +
        (state.signatureHtml ? `<div class="email-signature">${state.signatureHtml}</div>` : '') +
        (state.quotedHtml ? `<div class="email-quote">${state.quotedHtml}</div>` : '');

      await api.composeEmailMessage({
        accountId,
        toAddresses: state.to,
        ccAddresses: state.cc.length > 0 ? state.cc : undefined,
        bccAddresses: state.bcc.length > 0 ? state.bcc : undefined,
        subject: state.subject,
        bodyHtml: fullBody,
        bodyText: fullBody.replace(/<[^>]*>/g, ''),
        isDraft: true,
      });
      dirtyRef.current = false;
      traceEnd(traceId);
    } catch (err) {
      // No user-facing toast — auto-save runs every 30 s and a single
      // transient failure isn't worth a notification. But it does fly to
      // CloudWatch so we can spot a failing draft-save loop in telemetry.
      traceError(traceId, err instanceof Error ? err : String(err));
    } finally {
      draftSaveInFlightRef.current = false;
    }
  }, [accountId, state]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
    dirtyRef.current = false;
  }, []);

  const initReply = useCallback((message: EmailMessage | EmailMessageDetail, replyAll: boolean) => {
    const mode: ComposeMode = replyAll ? 'replyAll' : 'reply';
    const to: EmailAddress[] = [
      { email: message.fromAddress, name: message.fromName || undefined },
    ];
    let cc: EmailAddress[] = [];

    if (replyAll) {
      // Add all original To (except ourselves) and CC recipients
      const otherTo = (message.toAddresses || []).filter((a) => a.email !== message.fromAddress);
      cc = [...otherTo, ...(message.ccAddresses || [])];
    }

    const quotedHtml = `
        <br/><br/>
        <div style="border-left: 2px solid #ccc; padding-left: 12px; margin-left: 0; color: #666;">
          <p>On ${new Date(message.sentAt || message.createdAt).toLocaleString()}, ${message.fromName || message.fromAddress} wrote:</p>
          ${message.bodyHtml || `<p>${message.bodyText || ''}</p>`}
        </div>`;

    setState({
      ...INITIAL_STATE,
      to,
      cc,
      subject: message.subject.startsWith('Re:') ? message.subject : `Re: ${message.subject}`,
      mode,
      replyToMessageId: message.id,
      quotedHtml,
    });
  }, []);

  const initForward = useCallback((message: EmailMessage | EmailMessageDetail) => {
    const quotedHtml = `
      <br/><br/>
      <div>
        <p>---------- Forwarded message ----------</p>
        <p>From: ${message.fromName || ''} &lt;${message.fromAddress}&gt;</p>
        <p>Date: ${new Date(message.sentAt || message.createdAt).toLocaleString()}</p>
        <p>Subject: ${message.subject}</p>
        <p>To: ${(message.toAddresses || []).map((a) => a.name || a.email).join(', ')}</p>
        <br/>
        ${message.bodyHtml || `<p>${message.bodyText || ''}</p>`}
      </div>`;

    setState({
      ...INITIAL_STATE,
      subject: message.subject.startsWith('Fwd:') ? message.subject : `Fwd: ${message.subject}`,
      mode: 'forward',
      replyToMessageId: message.id,
      quotedHtml,
    });
  }, []);

  const setSignature = useCallback((html: string) => {
    setState((prev) => ({ ...prev, signatureHtml: html }));
  }, []);

  const send = useCallback(async () => {
    if (!accountId) return;
    if (state.to.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one recipient',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    const traceId = traceStart('email.send', {
      accountId,
      mode: state.mode,
      attachmentCount: state.attachments.length,
    });
    try {
      const fullBody =
        state.bodyHtml +
        (state.signatureHtml ? `<div class="email-signature">${state.signatureHtml}</div>` : '') +
        (state.quotedHtml ? `<div class="email-quote">${state.quotedHtml}</div>` : '');

      // Attachments uploaded during compose carry an `id` once the
      // backend mints their email_attachments row. Any attachment
      // still uploading is excluded — sending fires "Hold on,
      // attachments still uploading" via the parent UI; we just drop
      // the unfinished ones from this payload to keep the call safe.
      const attachmentIds = state.attachments
        .filter((a) => !a.uploading && a.id.length > 0)
        .map((a) => a.id);

      if (state.mode === 'reply' || state.mode === 'replyAll') {
        await api.replyToEmailMessage(state.replyToMessageId!, {
          accountId,
          bodyHtml: fullBody,
          bodyText: fullBody.replace(/<[^>]*>/g, ''),
          ccAddresses: state.cc.length > 0 ? state.cc : undefined,
          bccAddresses: state.bcc.length > 0 ? state.bcc : undefined,
          replyAll: state.mode === 'replyAll',
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
        });
      } else if (state.mode === 'forward') {
        await api.forwardEmailMessage(state.replyToMessageId!, {
          accountId,
          toAddresses: state.to,
          bodyHtml: fullBody,
          bodyText: fullBody.replace(/<[^>]*>/g, ''),
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
        });
      } else {
        await api.composeEmailMessage({
          accountId,
          toAddresses: state.to,
          ccAddresses: state.cc.length > 0 ? state.cc : undefined,
          bccAddresses: state.bcc.length > 0 ? state.bcc : undefined,
          subject: state.subject,
          bodyHtml: fullBody,
          bodyText: fullBody.replace(/<[^>]*>/g, ''),
          isDraft: false,
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
        });
      }

      traceEnd(traceId);
      toast({
        title: 'Message sent',
        description: 'Your message has been sent successfully.',
      });
      reset();
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      toast({
        title: 'Send failed',
        description: err instanceof Error ? err.message : 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  }, [accountId, state, toast, reset]);

  const saveDraft = useCallback(async () => {
    await saveDraftInternal();
    toast({ title: 'Draft saved' });
  }, [saveDraftInternal, toast]);

  const scheduleSend = useCallback(
    async (scheduledFor: string) => {
      if (!accountId) return;
      setSending(true);
      const traceId = traceStart('email.schedule', { accountId, scheduledFor });
      try {
        // First save as draft
        const fullBody =
          state.bodyHtml +
          (state.signatureHtml ? `<div class="email-signature">${state.signatureHtml}</div>` : '') +
          (state.quotedHtml ? `<div class="email-quote">${state.quotedHtml}</div>` : '');

        const msg = await api.composeEmailMessage({
          accountId,
          toAddresses: state.to,
          ccAddresses: state.cc.length > 0 ? state.cc : undefined,
          bccAddresses: state.bcc.length > 0 ? state.bcc : undefined,
          subject: state.subject,
          bodyHtml: fullBody,
          bodyText: fullBody.replace(/<[^>]*>/g, ''),
          isDraft: true,
        });

        await api.scheduleEmailSend({
          accountId,
          messageId: msg.id,
          scheduledFor,
        });

        traceEnd(traceId);
        toast({
          title: 'Message scheduled',
          description: `Will be sent at ${new Date(scheduledFor).toLocaleString()}`,
        });
        reset();
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        toast({
          title: 'Schedule failed',
          description: err instanceof Error ? err.message : 'Failed to schedule',
          variant: 'destructive',
        });
      } finally {
        setSending(false);
      }
    },
    [accountId, state, toast, reset],
  );

  const addAttachment = useCallback(
    async (file: File) => {
      if (!accountId) return;
      if (!personalHolderId) {
        toast({
          title: 'Upload unavailable',
          description: 'Personal Holder not resolved yet — please retry shortly.',
          variant: 'destructive',
        });
        return;
      }
      const tempId = crypto.randomUUID();
      const att: AttachmentFile = {
        id: tempId,
        documentId: null,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        uploading: true,
      };
      setState((prev) => ({ ...prev, attachments: [...prev.attachments, att] }));

      const traceId = traceStart('email.attachment.upload', {
        accountId,
        filename: file.name,
        mimeType: att.mimeType,
        sizeBytes: file.size,
      });

      try {
        // Canonical Vault upload: SHA-256 → upload-init → presigned PUT
        // → finalize. The hook returns the finalized vault doc row.
        const doc = await uploadVaultDocument.mutateAsync({
          holderId: personalHolderId,
          file,
        });

        // Bind the new vault doc to this email account / draft.
        const bind = await api.attachEmailDocument({
          accountId,
          documentId: doc.documentId,
        });

        setState((prev) => ({
          ...prev,
          attachments: prev.attachments.map((a) =>
            a.id === tempId
              ? {
                  ...a,
                  id: bind.attachment.id,
                  documentId: bind.attachment.documentId,
                  uploading: false,
                }
              : a,
          ),
        }));
        traceEnd(traceId);
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        setState((prev) => ({
          ...prev,
          attachments: prev.attachments.filter((a) => a.id !== tempId),
        }));
        toast({
          title: 'Upload failed',
          description: `Failed to upload ${file.name}`,
          variant: 'destructive',
        });
      }
    },
    [accountId, personalHolderId, toast, uploadVaultDocument],
  );

  const removeAttachment = useCallback(
    async (attachmentId: string) => {
      setState((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((a) => a.id !== attachmentId),
      }));
      if (accountId) {
        try {
          await api.deleteEmailAttachment(attachmentId, accountId);
        } catch {
          // Non-fatal
        }
      }
    },
    [accountId],
  );

  const insertTemplate = useCallback(
    (subject: string, bodyHtml: string) => {
      updateState({ subject, bodyHtml });
    },
    [updateState],
  );

  const insertSnippet = useCallback(
    (body: string) => {
      updateState({ bodyHtml: state.bodyHtml + body });
    },
    [state.bodyHtml, updateState],
  );

  return {
    ...state,
    sending,
    updateState,
    reset,
    initReply,
    initForward,
    setSignature,
    send,
    saveDraft,
    scheduleSend,
    addAttachment,
    removeAttachment,
    insertTemplate,
    insertSnippet,
  };
}
